import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Loader2, Plus, X, User, ArrowRight,
  TestTubes, ScanLine, Stethoscope, FlaskConical, Pill, FileText
} from 'lucide-react';
import { ChartCommunicationThread, ORIGIN_CONFIG, PRIORITY_CONFIG } from '../components/ChartCommunicationThread';
import { NewCommunicationForm } from '../components/NewCommunicationForm';

function _formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function MessagesBucketView({ onComplete, autoOpenId, onAutoOpenConsumed }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openConv, setOpenConv] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [originFilter, setOriginFilter] = useState('all');
  // Draggable window state
  const [windowPos, setWindowPos] = useState({ x: 0, y: 0 });
  const [windowFocused, setWindowFocused] = useState(true);
  const dragRef = React.useRef(null);
  const dragStart = React.useRef(null);

  const fetchConversations = useCallback(() => {
    api.get('/conversations').then(r => setConversations(r.data.conversations || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // 7K: Auto-open a specific conversation via deep link
  useEffect(() => {
    if (autoOpenId && !loading && conversations.length > 0) {
      handleOpenConv(autoOpenId);
      if (onAutoOpenConsumed) onAutoOpenConsumed();
    }
  }, [autoOpenId, loading, conversations.length]); // eslint-disable-line

  const handleOpenConv = async (convId) => {
    if (openConv?.id === convId && windowFocused) { setOpenConv(null); return; }
    if (openConv?.id === convId && !windowFocused) { setWindowFocused(true); return; }
    try {
      const res = await api.get(`/conversations/${convId}`);
      setOpenConv(res.data.conversation);
      setWindowPos({ x: 0, y: 0 }); // Reset position for new conversation
      setWindowFocused(true);
    } catch { toast.error('Failed to load conversation'); }
  };

  // Drag handlers
  const onDragStart = (e) => {
    if (e.target.closest('button, input, textarea, [role="button"]')) return;
    dragStart.current = { x: e.clientX - windowPos.x, y: e.clientY - windowPos.y };
    const onMove = (ev) => {
      if (dragStart.current) {
        setWindowPos({ x: ev.clientX - dragStart.current.x, y: ev.clientY - dragStart.current.y });
      }
    };
    const onUp = () => {
      dragStart.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleReply = async () => {};  // Legacy — handled by ChartCommunicationThread

  const handleResolve = async (convId) => {
    try {
      await api.post(`/conversations/${convId}/resolve`, { resolution_note: '' });
      setOpenConv(null);
      fetchConversations();
      onComplete();
      toast.success('Marked as resolved — thread stays open for additions');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve');
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try { await api.post('/conversations/seed-demo'); fetchConversations(); }
    catch {} finally { setSeeding(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  if (conversations.length === 0) {
    return (
      <div className="space-y-4 mt-1" data-testid="empty-messages">
        <div className="flex justify-end">
          <Button size="sm" variant={showNewMessage ? "default" : "outline"} className="h-7 gap-1.5 text-xs rounded-full"
            onClick={() => setShowNewMessage(!showNewMessage)} data-testid="provider-new-msg-btn">
            {showNewMessage ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            {showNewMessage ? 'Close' : 'New Message'}
          </Button>
        </div>
        {showNewMessage && (
          <ProviderNewMessage onClose={() => setShowNewMessage(false)} onCreated={() => { setShowNewMessage(false); fetchConversations(); onComplete(); }} />
        )}
        {!showNewMessage && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-2xl bg-muted/50 mb-4"><MessageSquare className="w-8 h-8 text-muted-foreground" /></div>
            <p className="text-sm font-medium mb-1">No active conversations</p>
            <p className="text-xs text-muted-foreground mb-4">Click "New Message" to send a message to the nurse team</p>
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-messages-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Load Demo Messages
            </Button>
          </div>
        )}
      </div>
    );
  }

  const STATE_STYLES = {
    active: 'border-l-amber-400 bg-amber-50/20',
    reopened: 'border-l-amber-400 bg-amber-50/20',
    resolved: 'border-l-emerald-400 bg-emerald-50/20 opacity-75',
    archived: 'border-l-slate-300 bg-muted/20 opacity-50',
  };

  const filteredConvs = originFilter === 'all'
    ? conversations
    : conversations.filter(c => (c.origin_tag || 'team') === originFilter);

  return (
    <div className="space-y-2 mt-1" data-testid="messages-bucket-view">
      {/* Header row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Origin filter chips */}
        <div className="flex gap-1 flex-wrap flex-1">
          <button onClick={() => setOriginFilter('all')}
            className={`text-[10px] px-2.5 py-1 rounded-full font-medium border transition-colors ${originFilter === 'all' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground border-border'}`}>
            All
          </button>
          {Object.entries(ORIGIN_CONFIG).map(([k, cfg]) => (
            <button key={k} onClick={() => setOriginFilter(k)}
              className={`text-[10px] px-2.5 py-1 rounded-full font-medium border transition-colors ${originFilter === k ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground border-border'}`}>
              {cfg.label}
            </button>
          ))}
        </div>
        {/* New Message */}
        <Button size="sm" variant={showNewMessage ? 'default' : 'outline'} className="h-7 gap-1.5 text-xs rounded-full shrink-0"
          onClick={() => setShowNewMessage(!showNewMessage)} data-testid="provider-new-msg-btn">
          {showNewMessage ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {showNewMessage ? 'Close' : 'New Message'}
        </Button>
      </div>

      {showNewMessage && (
        <NewCommunicationForm
          patientId={conversations[0]?.patient_id || ''}
          patientName={conversations[0]?.patient_name || ''}
          onCreated={() => { setShowNewMessage(false); fetchConversations(); onComplete(); }}
          onClose={() => setShowNewMessage(false)}
        />
      )}

      {filteredConvs.length === 0 && !showNewMessage && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="p-4 rounded-2xl bg-muted/50 mb-4"><MessageSquare className="w-8 h-8 text-muted-foreground" /></div>
          <p className="text-sm font-medium mb-1">No {originFilter === 'all' ? '' : ORIGIN_CONFIG[originFilter]?.label + ' '}conversations</p>
          <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-messages-btn">
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Load Demo Messages
          </Button>
        </div>
      )}

      {filteredConvs.map(conv => {
        const isOpen = openConv?.id === conv.id;
        const stateStyle = STATE_STYLES[conv.status] || STATE_STYLES.active;
        const origin = ORIGIN_CONFIG[conv.origin_tag || 'team'] || ORIGIN_CONFIG.team;
        const priority = PRIORITY_CONFIG[conv.priority || 'routine'] || PRIORITY_CONFIG.routine;
        const OriginIcon = origin.icon;

        return (
          <div key={conv.id} className={`rounded-lg border-l-4 border bg-card transition-all ${stateStyle} ${isOpen ? 'shadow-md' : 'hover:shadow-sm'}`}
            data-testid={`conv-card-${conv.id}`}>
            {/* Conversation preview card */}
            <button onClick={() => handleOpenConv(conv.id)} className="w-full text-left px-4 py-3 flex items-center gap-3">
              <div className={`p-1.5 rounded-lg border ${origin.color}`}>
                <OriginIcon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-foreground truncate">{conv.title}</span>
                  <span className={`inline-flex items-center gap-0.5 text-[8px] font-semibold px-1.5 py-0.5 rounded border ${priority.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                    {priority.label}
                  </span>
                  {conv.pending_with && conv.status !== 'resolved' && (
                    <Badge variant="outline" className={`text-[8px] h-3.5 ${conv.pending_with === 'provider' || (!['nurse','front_desk'].includes(conv.pending_with)) ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-500'}`}>
                      {conv.pending_with === 'provider' ? 'Your turn' : `With ${conv.pending_with_name || 'team'}`}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-muted-foreground font-medium">{conv.patient_name}</span>
                  <span className="text-[9px] text-muted-foreground">{conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}</span>
                  {conv.status === 'resolved' && (
                    <Badge className="text-[8px] bg-emerald-50 text-emerald-600 border-emerald-200">Resolved</Badge>
                  )}
                </div>
                {conv.last_message_preview && (
                  <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{conv.last_message_preview}</p>
                )}
              </div>
            </button>

            {/* Expanded thread — uses ChartCommunicationThread */}
            {isOpen && windowFocused && (
              <div className="border-t bg-background/50 rounded-b-lg" style={{ height: '420px' }}>
                <ChartCommunicationThread
                  conversation={openConv}
                  onUpdated={() => {
                    fetchConversations();
                    api.get(`/conversations/${conv.id}`).then(r => setOpenConv(r.data.conversation));
                    onComplete();
                  }}
                  autoFocus
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Floating thread window when dragged off card */}
      {openConv && !windowFocused && (
        <div ref={dragRef}
          className="fixed z-50 bg-card rounded-xl border shadow-2xl"
          style={{ left: windowPos.x || '30%', top: windowPos.y || '20%', width: 480, height: 520 }}
          onMouseDown={onDragStart}>
          <ChartCommunicationThread
            conversation={openConv}
            onUpdated={() => { fetchConversations(); onComplete(); }}
          />
        </div>
      )}
    </div>
  );
}
function ProviderNewMessage({ onClose, onCreated }) {
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [convType, setConvType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (patientSearch.length < 2) return;
    api.get(`/patients?search=${patientSearch}`).then(r => setPatients(r.data.patients || [])).catch(() => {});
  }, [patientSearch]);

  const SCENARIOS = [
    { value: 'task_nurse', label: 'Task for Nurse', desc: 'Assign a clinical task', icon: '📋',
      subjectHint: 'e.g., "Call patient re: lab results"',
      bodyHint: 'What needs to be done, any specific instructions...' },
    { value: 'med_refill', label: 'Refill Approval', desc: 'Approve or deny a refill', icon: '💊',
      subjectHint: 'e.g., "Approve lisinopril refill — call pharmacy"',
      bodyHint: 'Medication, dose, refills, pharmacy instructions...' },
    { value: 'lab_review', label: 'Lab Follow-up', desc: 'Follow up on lab result', icon: '🧪',
      subjectHint: 'e.g., "A1c 8.2% — schedule patient follow-up"',
      bodyHint: 'Lab result, what needs to happen, patient notification...' },
    { value: 'patient_callback', label: 'Patient Callback', desc: 'Have nurse call patient', icon: '📞',
      subjectHint: 'e.g., "Call Mrs. Tester — discuss med adjustment"',
      bodyHint: 'What to tell the patient, any decisions made...' },
    { value: 'general', label: 'Other', desc: 'General message', icon: '✉️',
      subjectHint: 'Brief subject',
      bodyHint: 'Message details...' },
  ];

  const activeScenario = SCENARIOS.find(s => s.value === convType);

  const handleSubmit = async () => {
    if (!selectedPatient || !convType || !title || !body) return;
    setSubmitting(true);
    try {
      await api.post('/conversations', {
        patient_id: selectedPatient.id,
        title,
        conversation_type: convType === 'task_nurse' ? 'general' : convType === 'patient_callback' ? 'patient_question' : convType,
        body,
        pending_with: 'nurse',
      });
      toast.success('Message sent to nurse');
      onCreated();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-2xl border border-blue-200 bg-white shadow-sm p-4 space-y-3" data-testid="provider-new-message">
      {/* Step 1: scenario picker */}
      {!convType ? (
        <div>
          <p className="text-xs text-slate-500 mb-2">What do you need?</p>
          <div className="grid grid-cols-3 gap-2">
            {SCENARIOS.map(s => (
              <button key={s.value} onClick={() => setConvType(s.value)}
                className="text-left p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all"
                data-testid={`prov-scenario-${s.value}`}>
                <span className="text-base block mb-0.5">{s.icon}</span>
                <span className="text-[11px] font-semibold text-slate-700 block">{s.label}</span>
                <span className="text-[9px] text-slate-400">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Selected scenario */}
          <div className="flex items-center gap-2">
            <span className="text-base">{activeScenario?.icon}</span>
            <span className="text-xs font-semibold text-slate-700">{activeScenario?.label}</span>
            <button onClick={() => setConvType('')} className="text-[9px] text-blue-500 ml-1">change</button>
          </div>

          {/* Patient */}
          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Patient</p>
            {selectedPatient ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-semibold text-primary">
                  {selectedPatient.name?.first?.[0]}{selectedPatient.name?.last?.[0]}
                </div>
                <span className="text-sm font-medium">{selectedPatient.name?.first} {selectedPatient.name?.last}</span>
                <Badge variant="outline" className="text-[9px] font-mono">{selectedPatient.mrn}</Badge>
                <button onClick={() => setSelectedPatient(null)} className="text-[9px] text-slate-400 ml-auto">change</button>
              </div>
            ) : (
              <div>
                <input type="text" placeholder="Search patient name..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 placeholder:text-slate-300" autoFocus />
                {patients.length > 0 && (
                  <div className="border rounded-xl mt-1 max-h-28 overflow-y-auto shadow-sm">
                    {patients.slice(0, 5).map(p => (
                      <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(''); setPatients([]); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0 flex items-center gap-2">
                        <span className="font-medium">{p.name?.first} {p.name?.last}</span>
                        <span className="text-slate-400 ml-auto">{p.mrn}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subject + Message */}
          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Subject</p>
            <input type="text" placeholder={activeScenario?.subjectHint || 'Subject'} value={title}
              onChange={e => setTitle(e.target.value)} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2" />
          </div>
          <div>
            <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Instructions</p>
            <Textarea placeholder={activeScenario?.bodyHint || 'Details...'} value={body} onChange={e => setBody(e.target.value)}
              className="min-h-[72px] text-sm resize-none rounded-xl border-slate-200" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!selectedPatient || !title || !body || submitting}
              className="gap-1.5" data-testid="provider-send-msg-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to Nurse
            </Button>
          </div>
        </>
      )}
    </div>
  );
}




/* ═══════════════════════════════════════
   DOCUMENT SIGN-OFF OVERLAY — Opens in My Day, no navigation
   ═══════════════════════════════════════ */


function AttachButton({ conversationId, patientId, onAttached }) {
  const [open, setOpen] = useState(false);
  const [artifacts, setArtifacts] = useState(null);
  const [activeTab, setActiveTab] = useState('correspondence');
  const [search, setSearch] = useState('');

  const loadArtifacts = async () => {
    try {
      const res = await api.get(`/patients/${patientId}/attachable-artifacts`);
      setArtifacts(res.data);
    } catch { setArtifacts({ correspondence: [], labs: [], medications: [], notes: [] }); }
  };

  const handleAttach = async (type, id, label) => {
    try {
      await api.post(`/conversations/${conversationId}/link-artifact`, {
        artifact_type: type, artifact_id: id, label,
      });
      toast.success(`Attached: ${label}`);
      setOpen(false);
      setSearch('');
      onAttached();
    } catch { toast.error('Attach failed'); }
  };

  const TABS = [
    { key: 'correspondence', label: 'Correspondence', icon: FileText, color: 'text-amber-500' },
    { key: 'labs', label: 'Labs', icon: FlaskConical, color: 'text-blue-500' },
    { key: 'medications', label: 'Meds', icon: Pill, color: 'text-violet-500' },
    { key: 'notes', label: 'Notes', icon: FileText, color: 'text-emerald-500' },
  ];

  const filteredItems = (artifacts?.[activeTab] || []).filter(item =>
    !search || item.label?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full"
        onClick={() => { setOpen(true); if (!artifacts) loadArtifacts(); }}
        data-testid="attach-btn">
        <Plus className="w-2.5 h-2.5" /> Attach
      </Button>

      {/* WhatsApp-style centered attachment picker overlay */}
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" onClick={() => { setOpen(false); setSearch(''); }}>
          <div className="absolute inset-0 bg-black/10" />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150"
            onClick={e => e.stopPropagation()} data-testid="attachment-picker">
            {/* Header */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Attach from Chart</h3>
              <button onClick={() => { setOpen(false); setSearch(''); }} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-2.5">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search documents..." autoFocus
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 placeholder:text-slate-300 focus:bg-white focus:border-slate-300 transition-colors"
                data-testid="attach-search" />
            </div>

            {/* Category tabs — icon-forward like WhatsApp */}
            <div className="px-4 pb-2 flex justify-center gap-3">
              {TABS.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${activeTab === t.key ? 'bg-slate-100 shadow-sm' : 'hover:bg-slate-50'}`}
                    data-testid={`attach-tab-${t.key}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeTab === t.key ? 'bg-white shadow-sm' : 'bg-slate-100'}`}>
                      <Icon className={`w-5 h-5 ${t.color}`} />
                    </div>
                    <span className={`text-[9px] font-medium ${activeTab === t.key ? 'text-slate-700' : 'text-slate-400'}`}>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Items list */}
            <div className="px-4 pb-4 max-h-52 overflow-y-auto space-y-1">
              {!artifacts ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
              ) : filteredItems.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No {activeTab} items found</p>
              ) : (
                filteredItems.map(item => {
                  const TabIcon = TABS.find(t => t.key === activeTab)?.icon || FileText;
                  const tabColor = TABS.find(t => t.key === activeTab)?.color || 'text-slate-400';
                  return (
                    <button key={item.id} onClick={() => handleAttach(item.type || activeTab, item.id, item.label)}
                      className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors flex items-center gap-3 group"
                      data-testid={`attach-item-${item.id}`}>
                      <div className={`w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-white group-hover:shadow-sm transition-all`}>
                        <TabIcon className={`w-4 h-4 ${tabColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-700 truncate">{item.label}</p>
                        <p className="text-[10px] text-slate-400">{item.status || item.type?.replace(/_/g, ' ') || activeTab}</p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}




/* ═══════════════════════════════════════
   PROVIDER NEW MESSAGE — Inline scenario form (mirrors nurse)
   ═══════════════════════════════════════ */



export { MessagesBucketView, ProviderNewMessage, AttachButton };
